/**
 * Secure Public API Client - Main Entry Point
 *
 * A comprehensive, security-hardened API client designed specifically for public websites.
 * Includes protection against XSS, CSRF, injection attacks, and other security threats.
 *
 * Security Level: PUBLIC-ONLY with enterprise-grade protection
 */

// Main API Client
import { APIClient } from './api-client.js';
import { HTTPClient } from './client/http-client.js';

// Services (Read-Only)
import { ConfigurationsService } from './services/configurations-service.js';
import { IssuesService } from './services/issues-service.js';
import { OrganizationsService } from './services/organizations-service.js';
import { SystemService } from './services/system-service.js';

// Security Modules
import { APIError } from './errors/api-error.js';
import * as CSRFProtection from './security/csrf-protection.js';
import * as InputSanitizer from './security/input-sanitizer.js';
import * as RequestValidator from './security/request-validator.js';

// Configuration
import {
    API_CONFIG,
    API_ENDPOINTS,
    CSP_CONFIG,
    SECURITY_CONFIG,
    SECURITY_STATUS,
    getCSPHeader,
    getSecurityRecommendations,
    isSecureEnvironment
} from './config/api-config.js';

// Utilities
import * as requestInterceptors from './utils/request-interceptors.js';

/**
 * Create default secure API client instance
 */
const createDefaultClient = () => {
  const config = {
    baseURL: API_CONFIG.BASE_URL,
    timeout: API_CONFIG.timeout,
    headers: API_CONFIG.headers,
    enableCSRF: true,
    enableRateLimit: true,
    maxConcurrentRequests: 5
  };

  return new APIClient(config);
};

// Default client instance
const apiClient = createDefaultClient();

/**
 * Security validation on module load
 */
function validateSecuritySetup() {
  const recommendations = getSecurityRecommendations();

  if (recommendations.length > 0) {
    console.group('🔒 API Client Security Status');

    recommendations.forEach(rec => {
      const icon = rec.type === 'critical' ? '🚨' : rec.type === 'warning' ? '⚠️' : 'ℹ️';
      console.log(`${icon} ${rec.message}`);
    });

    console.log('✅ Security Level: PUBLIC-ONLY');
    console.log('✅ Input Sanitization: ENABLED');
    console.log('✅ CSRF Protection: ENABLED');
    console.log('✅ Rate Limiting: ENABLED');
    console.log('✅ Request Validation: ENABLED');
    console.log('✅ HTTPS Enforcement: ' + (SECURITY_CONFIG.enforceHTTPS ? 'ENABLED' : 'DEVELOPMENT ONLY'));

    console.groupEnd();
  }
}

// Run security validation
if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'test') {
  validateSecuritySetup();
}

// Main exports
export default apiClient;

export {
    // Core API Client
    APIClient,
    // Security Components
    APIError,
    // Configuration & Status
    API_CONFIG, API_ENDPOINTS,
    CSP_CONFIG, CSRFProtection, ConfigurationsService, HTTPClient, InputSanitizer,
    // Individual Services (Read-Only)
    IssuesService,
    OrganizationsService, RequestValidator, SECURITY_CONFIG, SECURITY_STATUS, SystemService,
    // Factory function for custom configurations
    createDefaultClient, getCSPHeader, getSecurityRecommendations, isSecureEnvironment,
    // Utilities
    requestInterceptors
};

/**
 * Security utilities - Direct access
 */
export const {
  // Input Sanitization
  htmlEncode,
  sanitizeString,
  sanitizeQueryParams,
  sanitizeRequestBody,
  validateUrl,
  checkRateLimit
} = InputSanitizer;

export const {
  // Request Validation
  validateMethod,
  validateHeaders,
  validateRequestUrl,
  validateRequestSize,
  validateQueryParameters,
  validateRequest,
  validateResponse,
  createSecurityMiddleware
} = RequestValidator;

export const {
  // CSRF Protection
  addCSRFToken,
  validateCSRFResponse,
  getCSRFToken,
  shouldIncludeCSRF,
  validateRequestOrigin,
  createCSRFMiddleware,
  configureSameSiteCookies,
  doubleSubmitCSRF
} = CSRFProtection;

/**
 * Quick access to API services
 */
export const api = {
  issues: apiClient.issues,
  organizations: apiClient.organizations,
  configurations: apiClient.configurations,
  system: apiClient.system, // Note: All methods throw errors for public safety

  // Utility methods
  testConnection: () => apiClient.testConnection(),
  getSecurityStatus: () => apiClient.getHttpClient().getSecurityStatus(),
  getServices: () => apiClient.getServices()
};

/**
 * Security monitoring interface
 */
export const security = {
  status: SECURITY_STATUS,
  config: SECURITY_CONFIG,
  csp: CSP_CONFIG,
  recommendations: getSecurityRecommendations(),
  isSecure: isSecureEnvironment(),
  validateSetup: validateSecuritySetup
};

/**
 * Development utilities
 */
export const dev = {
  // Only available in development
  ...(process.env.NODE_ENV === 'development' && {
    testSecurity: async () => {
      console.group('🧪 Security Tests');

      try {
        // Test input sanitization
        const malicious = "<script>alert('xss')</script>";
        const sanitized = InputSanitizer.sanitizeString(malicious);
        console.log('✅ Input Sanitization:', sanitized);

        // Test rate limiting
        console.log('⏱️ Testing rate limiting...');
        const promises = Array.from({ length: 5 }, () => apiClient.issues.getAll());
        await Promise.all(promises);
        console.log('✅ Rate limiting functional');

        // Test CSRF tokens
        const token = CSRFProtection.getCSRFToken();
        console.log('✅ CSRF Token Generated:', token.substring(0, 10) + '...');

        // Test security status
        const status = apiClient.getHttpClient().getSecurityStatus();
        console.log('✅ Security Status:', status);

      } catch (error) {
        console.log('⚠️ Security Test Result:', error.message);
      }

      console.groupEnd();
    },

    logSecurityEvents: true,
    bypassRateLimit: false // Always false for security
  })
};

// Clean up development utilities in production
if (process.env.NODE_ENV === 'production') {
  Object.freeze(dev);
}